"use client";

import Image from "next/image";
import Text from "@components/atoms/Text";
import assetLoader from "@utils/internalImageLoder";
import { CarCardProps } from "./CarCard.types";
import styles from "./CarCard.module.scss";
import { formatCurrencyINR, maskAmount } from "@utils/numberFormatter";
import { formatDeliveryDate } from "@utils/formatDate";
import { getCurrencyWithLakh, toKebabCase } from "@utils/stringFormatters";
import { CarCardClientWrapper } from "./CarCardClientWrapper";
import ImageResponsive from "@components/atoms/ImageResponsive";
import { getCarAvailability } from "@utils/carAvailability";

export const CarCard = ({
  modelName,
  brandName,
  cityName,
  savings,
  minPrice,
  modelExpressDeliveryFlag = true,
  waitingPeriod,
  isLoggedIn,
  slug,
  carouselSection,
  imageLoading = "lazy",
  status,
  pageType,
  backgroundIndex = 1,
}: CarCardProps) => {
  const isDiscontinued = status === "out_of_production";
  const nonSellable = status === "non_sellable";
  const isSellable = !nonSellable && !isDiscontinued;

  return (
    <CarCardClientWrapper
      slug={slug}
      modelName={modelName}
      brandName={brandName}
      cityName={cityName}
      carouselSection={carouselSection}
      carAvailability={getCarAvailability(status)}
      pageType={pageType}
    >
      <div className={styles.card}>
        <div className={styles.imageWrapper}>
          <Image
            src={`${assetLoader()}/icons/background/SimilarCardBackground${backgroundIndex}.webp`}
            alt="Background"
            fill
            className="object-cover"
            {...(imageLoading === "priority"
              ? { priority: true }
              : { loading: "lazy" })}
          />

          {modelExpressDeliveryFlag && (
            <div className={styles.expressDelivery}>
              <Image
                src={`${assetLoader()}/icons/ExpressDelivery.svg`}
                alt={"Express delivery"}
                className={styles.chevronDown}
                width={16}
                height={16}
                loading="lazy"
              />
              {"Express delivery"}
              {isLoggedIn && waitingPeriod
                ? ` by ${formatDeliveryDate(parseInt(waitingPeriod))}`
                : ""}
            </div>
          )}

          <div className={styles.carImageContainer}>
            <ImageResponsive
              filePath={`${toKebabCase(brandName)}/${toKebabCase(modelName)}`}
              imageTag={`Hero-Transparent`}
              alt={modelName}
              width={300}
              height={120}
              className={styles.carImage}
              {...(imageLoading === "priority"
                ? { priority: true }
                : { loading: "lazy" })}
            />
          </div>
        </div>

        <div className={styles.cardContent}>
          <Text
            className={styles.modelName}
          >{`${brandName} ${modelName}`}</Text>
          <Text className={styles.cityNote}>
            {`On-road price in ${cityName} starts from`}
          </Text>
          <div className={styles.priceWrapper}>
            <Text className={styles.minPrice}>
              {`${getCurrencyWithLakh(minPrice)}`}
            </Text>
            {isSellable && (
              <Text className={styles.savings}>
                {`Get up to ${
                  isLoggedIn ? formatCurrencyINR(savings) : maskAmount(savings)
                } off.`}
              </Text>
            )}
            {!isSellable && (
              <div className={styles.discontinuedBadge}>
                {isDiscontinued && (
                  <>
                    <Image
                      src={`${assetLoader()}/icons/close_circular_light.svg`}
                      alt="Discontinued"
                      width={12}
                      height={12}
                      className={styles.closeIcon}
                    />
                    <Text className={styles.discontinuedText}>
                      Discontinued
                    </Text>
                  </>
                )}
                {nonSellable && (
                  <>
                    <Image
                      src={`${assetLoader()}/icons/Carousal/Exclamation.svg`}
                      alt="Unavailable"
                      width={12}
                      height={12}
                      className={styles.closeIcon}
                    />
                    <Text className={styles.discontinuedText}>Unavailable</Text>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </CarCardClientWrapper>
  );
};
